import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { Clock, CheckCircle, XCircle, Truck, Trash2 } from 'lucide-react';

interface OrderItem {
  id?: string;
  retailer: string;
  url: string;
  price: number;
  category: string;
  quantity?: number;
  size?: string | null;
  color?: string | null;
  discount_code?: string | null;
  image_filename?: string | null;
}

interface Order {
  id: string;
  user_id: string;
  status: string;
  total_amount: number;
  currency: string;
  payment_method?: string;
  created_at: string;
  items: OrderItem[];
  tracking_number?: string | null;
  carrier?: string | null;
  tracking_url?: string | null;
  delivery_status?: string | null;
  invoice_status?: string;
  invoice_rejected_reason?: string | null;
  reward_credited?: boolean;
  reward_credited_amount?: number | null;
  shipping_fee_amount?: number | null;
  shipping_fee_payment_method?: string | null;
  shipping_fee_paid?: boolean;
  admin_base_shipping_fee?: number | null;
  package_id?: string | null;
}

interface PackageInfo {
  id: string;
  order_ids: string[];
  shipping_fee_amount: number | null;
  shipping_fee_payment_method: string | null;
  shipping_fee_paid: boolean;
  admin_base_shipping_fee?: number | null;
}

interface TrackingForm {
  tracking_number: string;
  carrier: string;
  tracking_url: string;
  delivery_status: string;
}

interface ShipForm {
  tracking_number: string;
  carrier: string;
  tracking_url: string;
}

interface InvoiceForm {
  compliant: boolean;
  rejection_reason: string;
  actual_invoice_amount: string;
}

interface EditItemForm {
  id?: string;
  retailer: string;
  url: string;
  price: string;
  category: string;
  quantity: string;
  size: string;
  color: string;
  discount_code: string;
}

interface EditOrderForm {
  items: EditItemForm[];
  total_amount: string;
  shipping_fee_amount: string;
  shipping_fee_payment_method: string;
}

const emptyTrackingForm: TrackingForm = { tracking_number: '', carrier: '', tracking_url: '', delivery_status: '' };
const emptyShipForm: ShipForm = { tracking_number: '', carrier: '', tracking_url: '' };
const emptyInvoiceForm: InvoiceForm = { compliant: true, rejection_reason: '', actual_invoice_amount: '' };

const statusColors: Record<string, string> = {
  pending_preauth: '#F59E0B',
  preauthorized: '#8B5CF6',
  confirmed: '#0891B2',
  captured: '#2563EB',
  shipped: '#EA580C',
  delivered: '#16A34A',
  released: '#EF4444',
  fulfilled: '#10B981',
};

const AdminProcurementRequests: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [actionOrderId, setActionOrderId] = useState<string | null>(null);
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(null);
  const [trackingForm, setTrackingForm] = useState<TrackingForm>(emptyTrackingForm);
  const [savingTracking, setSavingTracking] = useState(false);
  const [shipOrderId, setShipOrderId] = useState<string | null>(null);
  const [shipForm, setShipForm] = useState<ShipForm>(emptyShipForm);
  const [savingShip, setSavingShip] = useState(false);
  const [invoiceOrderId, setInvoiceOrderId] = useState<string | null>(null);
  const [invoiceForm, setInvoiceForm] = useState<InvoiceForm>(emptyInvoiceForm);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [packages, setPackages] = useState<Record<string, PackageInfo>>({});
  const [actionPackageId, setActionPackageId] = useState<string | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [itemImageUrls, setItemImageUrls] = useState<Record<string, string>>({});
  const [deleteOrderId, setDeleteOrderId] = useState<string | null>(null);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [editOrderId, setEditOrderId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditOrderForm | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [feeTarget, setFeeTarget] = useState<{ type: 'order' | 'package'; id: string } | null>(null);
  const [feeForm, setFeeForm] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.get(API.listAdminOrders(), { headers });
      const loadedOrders = response.data as Order[];
      setOrders(loadedOrders);

      const packageIds = Array.from(new Set(loadedOrders.map((o) => o.package_id).filter(Boolean))) as string[];
      const fetchedPackages = await Promise.all(
        packageIds.map((id) => axios.get(API.getPackage(id), { headers }).then((res) => res.data).catch(() => null)),
      );
      setPackages(Object.fromEntries(fetchedPackages.filter(Boolean).map((pkg: PackageInfo) => [pkg.id, pkg])));
    } catch (err: any) {
      console.error('Failed to load smart orders:', err);
      const detail = err.response?.data?.detail || err.message;
      setError(`Failed to load smart orders: ${detail}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) fetchData();
  }, [fetchData, user]);

  // BDD Scenario 8: item images require an Authorization header, so they're
  // fetched as blobs and shown via object URLs (same pattern as Orders.tsx).
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    orders.forEach((order) => {
      order.items.forEach((item) => {
        if (item.image_filename && item.id && !itemImageUrls[item.id]) {
          axios
            .get(API.getOrderItemImage(order.id, item.id), { headers, responseType: 'blob' })
            .then((res) => setItemImageUrls((current) => ({ ...current, [item.id!]: window.URL.createObjectURL(res.data) })))
            .catch(() => {});
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const detailOrder = useMemo(() => orders.find((o) => o.id === detailOrderId) || null, [orders, detailOrderId]);

  const filteredOrders = useMemo(() => {
    if (!statusFilter) return orders;
    return orders.filter((order) => order.status === statusFilter);
  }, [orders, statusFilter]);

  const stats = useMemo(() => ({
    pending: orders.filter((order) => order.status === 'pending_preauth' || order.status === 'preauthorized').length,
    inProgress: orders.filter((order) => ['confirmed', 'captured', 'shipped'].includes(order.status)).length,
    delivered: orders.filter((order) => order.status === 'delivered').length,
    released: orders.filter((order) => order.status === 'released').length,
  }), [orders]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const runAction = async (order: Order, confirmMessage: string | null, request: () => Promise<any>, successMessage: string, failureMessage: string) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setActionOrderId(order.id);
    setError('');
    setMessage('');
    try {
      await request();
      setMessage(successMessage);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || failureMessage);
    } finally {
      setActionOrderId(null);
    }
  };

  const handleConfirmBankTransfer = (order: Order) => runAction(
    order,
    `Confirm that the bank transfer for order #${order.id.split('-')[0].toUpperCase()} (£${order.total_amount.toFixed(2)}) has arrived?`,
    () => axios.post(API.confirmBankTransfer(order.id), {}, { headers: authHeaders() }),
    `Bank transfer confirmed for order #${order.id.split('-')[0].toUpperCase()}.`,
    'Failed to confirm bank transfer.',
  );

  const handleConfirmShippingBankTransfer = (order: Order) => runAction(
    order,
    `Confirm that the shipping fee bank transfer for order #${order.id.split('-')[0].toUpperCase()} (£${(order.shipping_fee_amount ?? 0).toFixed(2)}) has arrived?`,
    () => axios.post(API.confirmOrderShippingBankTransfer(order.id), {}, { headers: authHeaders() }),
    `Shipping fee bank transfer confirmed for order #${order.id.split('-')[0].toUpperCase()}.`,
    'Failed to confirm shipping fee bank transfer.',
  );

  const handleConfirmPackageBankTransfer = async (pkg: PackageInfo) => {
    if (!window.confirm(`Confirm that the shipping fee bank transfer for the ${pkg.order_ids.length}-order package (£${(pkg.shipping_fee_amount ?? 0).toFixed(2)}) has arrived?`)) return;
    setActionPackageId(pkg.id);
    setError('');
    setMessage('');
    try {
      await axios.post(API.confirmPackageShippingBankTransfer(pkg.id), {}, { headers: authHeaders() });
      setMessage(`Shipping fee bank transfer confirmed for the combined package.`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to confirm shipping fee bank transfer.');
    } finally {
      setActionPackageId(null);
    }
  };

  const handleConfirmOrder = (order: Order) => runAction(
    order,
    null,
    () => axios.post(API.confirmOrder(order.id), {}, { headers: authHeaders() }),
    `Order #${order.id.split('-')[0].toUpperCase()} confirmed as a legitimate purchase request.`,
    'Failed to confirm order.',
  );

  const handleCapture = (order: Order) => runAction(
    order,
    `Capture payment for order #${order.id.split('-')[0].toUpperCase()} (£${order.total_amount.toFixed(2)})? This charges the customer's card.`,
    () => axios.post(API.capturePayment(order.id), {}, { headers: authHeaders() }),
    `Payment captured for order #${order.id.split('-')[0].toUpperCase()}.`,
    'Failed to capture payment.',
  );

  const handleRelease = (order: Order) => runAction(
    order,
    `Release the pre-authorization for order #${order.id.split('-')[0].toUpperCase()}? This cancels the order and releases the customer's held funds.`,
    () => axios.post(API.releasePreauth(order.id), {}, { headers: authHeaders() }),
    `Pre-authorization released for order #${order.id.split('-')[0].toUpperCase()}.`,
    'Failed to release pre-authorization.',
  );

  const handleMarkDelivered = (order: Order) => runAction(
    order,
    `Mark order #${order.id.split('-')[0].toUpperCase()} as delivered on the customer's behalf?`,
    () => axios.post(API.confirmDelivery(order.id), {}, { headers: authHeaders() }),
    `Order #${order.id.split('-')[0].toUpperCase()} marked delivered.`,
    'Failed to confirm delivery.',
  );

  const handleDeleteOrder = async () => {
    if (!deleteOrderId) return;
    setDeletingOrder(true);
    setError('');
    setMessage('');
    try {
      await axios.delete(API.adminDeleteOrder(deleteOrderId), { headers: authHeaders() });
      setMessage('Smart Order deleted.');
      setDeleteOrderId(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete order.');
    } finally {
      setDeletingOrder(false);
    }
  };

  const openTrackingModal = (order: Order) => {
    setTrackingOrderId(order.id);
    setTrackingForm({
      tracking_number: order.tracking_number || '',
      carrier: order.carrier || '',
      tracking_url: order.tracking_url || '',
      delivery_status: order.delivery_status || '',
    });
  };

  const submitTracking = async () => {
    if (!trackingOrderId) return;
    setSavingTracking(true);
    setError('');
    try {
      await axios.put(API.updateOrderTracking(trackingOrderId), trackingForm, { headers: authHeaders() });
      setMessage('Tracking details updated.');
      setTrackingOrderId(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update tracking.');
    } finally {
      setSavingTracking(false);
    }
  };

  const openShipModal = (order: Order) => {
    setShipOrderId(order.id);
    setShipForm(emptyShipForm);
  };

  const submitShip = async () => {
    if (!shipOrderId) return;
    setSavingShip(true);
    setError('');
    try {
      await axios.post(API.shipOrder(shipOrderId), shipForm, { headers: authHeaders() });
      setMessage('Order shipped and tracking recorded.');
      setShipOrderId(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to ship order.');
    } finally {
      setSavingShip(false);
    }
  };

  const openInvoiceModal = (order: Order) => {
    setInvoiceOrderId(order.id);
    // BDD 1-A line 101 (「發票上之商品採購金額」): the reward is computed off
    // the invoice's real amount, which can genuinely differ from the
    // customer's original quote since the buying team purchases on their
    // behalf. Prefilled with the sum of the order's own item prices x
    // quantities — matching the backend's real no-override default
    // (orders/service.py::verify_invoice) and what the customer's own
    // reward estimate was actually computed from. NOT order.total_amount:
    // for an OrderPortal.tsx order that's `rawItemTotal*0.92+50` (Incord's
    // own service-fee markup), a different figure entirely (todo.txt 23.1,
    // found during a seventh frontend-coverage audit — round 5's fix
    // (21.2) prefilled with total_amount, which was itself wrong).
    const itemsRawTotal = order.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    setInvoiceForm({ ...emptyInvoiceForm, actual_invoice_amount: itemsRawTotal.toFixed(2) });
  };

  const submitInvoice = async () => {
    if (!invoiceOrderId) return;
    setSavingInvoice(true);
    setError('');
    try {
      const response = await axios.post(
        API.verifyInvoice(invoiceOrderId),
        {
          compliant: invoiceForm.compliant,
          rejection_reason: invoiceForm.compliant ? undefined : invoiceForm.rejection_reason,
          actual_invoice_amount: invoiceForm.compliant && invoiceForm.actual_invoice_amount ? Number(invoiceForm.actual_invoice_amount) : undefined,
        },
        { headers: authHeaders() },
      );
      setMessage(
        invoiceForm.compliant
          ? `Invoice verified — £${(response.data.reward_credited_amount ?? 0).toFixed(2)} Bonus Reward credited.`
          : 'Invoice rejected — no Bonus Reward released for this order.',
      );
      setInvoiceOrderId(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify invoice.');
    } finally {
      setSavingInvoice(false);
    }
  };

  const editOrder = useMemo(() => orders.find((o) => o.id === editOrderId) || null, [orders, editOrderId]);
  const editContentAllowed = editOrder ? ['pending_preauth', 'preauthorized', 'confirmed'].includes(editOrder.status) : false;
  const editShippingFeeAllowed = editOrder ? !editOrder.shipping_fee_paid : false;

  const openEditModal = (order: Order) => {
    setEditOrderId(order.id);
    setEditForm({
      items: order.items.map((item) => ({
        id: item.id,
        retailer: item.retailer,
        url: item.url,
        price: String(item.price),
        category: item.category,
        quantity: String(item.quantity ?? 1),
        size: item.size || '',
        color: item.color || '',
        discount_code: item.discount_code || '',
      })),
      total_amount: String(order.total_amount),
      shipping_fee_amount: order.shipping_fee_amount != null ? String(order.shipping_fee_amount) : '',
      shipping_fee_payment_method: order.shipping_fee_payment_method || '',
    });
  };

  const updateEditItem = (index: number, field: keyof EditItemForm, value: string) => {
    setEditForm((current) => {
      if (!current) return current;
      const items = current.items.slice();
      items[index] = { ...items[index], [field]: value };
      return { ...current, items };
    });
  };

  const submitEdit = async () => {
    if (!editOrderId || !editForm || !editOrder) return;
    setSavingEdit(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {};
      if (editContentAllowed) {
        payload.items = editForm.items.map((item) => ({
          retailer: item.retailer,
          url: item.url,
          price: Number(item.price),
          category: item.category,
          quantity: Number(item.quantity) || 1,
          size: item.size || null,
          color: item.color || null,
          discount_code: item.discount_code || null,
        }));
        payload.total_amount = Number(editForm.total_amount);
      }
      if (editShippingFeeAllowed) {
        payload.shipping_fee_amount = editForm.shipping_fee_amount ? Number(editForm.shipping_fee_amount) : null;
        payload.shipping_fee_payment_method = editForm.shipping_fee_payment_method || null;
      }
      await axios.put(API.adminUpdateOrder(editOrderId), payload, { headers: authHeaders() });
      setMessage(`Order #${editOrderId.split('-')[0].toUpperCase()} updated.`);
      setEditOrderId(null);
      setEditForm(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update order.');
    } finally {
      setSavingEdit(false);
    }
  };

  // BDD Scenario 1-A: Admin packs the order/package and enters the real
  // carrier cost, unlocking the customer's "Pay Shipping Fee" step.
  const openFeeModal = (type: 'order' | 'package', id: string, currentFee?: number | null) => {
    setFeeTarget({ type, id });
    setFeeForm(currentFee != null ? String(currentFee) : '');
  };

  const submitFee = async () => {
    if (!feeTarget || !feeForm || Number(feeForm) <= 0) return;
    setSavingFee(true);
    setError('');
    try {
      const endpoint = feeTarget.type === 'order' ? API.adminSetOrderShippingFee(feeTarget.id) : API.adminSetPackageShippingFee(feeTarget.id);
      await axios.put(endpoint, { base_shipping_fee: Number(feeForm) }, { headers: authHeaders() });
      setMessage('Shipping fee saved — the customer can now pay it.');
      setFeeTarget(null);
      setFeeForm('');
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save the shipping fee.');
    } finally {
      setSavingFee(false);
    }
  };

  if (!user || !user.is_admin) return <div>Access Denied</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Smart Orders</h1>
        <button className="btn btn-outline" onClick={fetchData}>Refresh</button>
      </div>

      {(error || message) && (
        <div style={{
          marginBottom: '24px', padding: '16px', borderRadius: '8px', fontSize: '14px',
          background: error ? '#FEF2F2' : '#F0FDF4',
          color: error ? '#991B1B' : '#166534',
          border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
        }}>
          {error || message}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Awaiting Review', value: stats.pending, color: '#F59E0B', bg: '#FEF9C3', icon: <Clock size={18} /> },
          { label: 'In Progress', value: stats.inProgress, color: '#8B5CF6', bg: '#F3E8FF', icon: <CheckCircle size={18} /> },
          { label: 'Delivered', value: stats.delivered, color: '#16A34A', bg: '#DCFCE7', icon: <Truck size={18} /> },
          { label: 'Released', value: stats.released, color: '#EF4444', bg: '#FEF2F2', icon: <XCircle size={18} /> },
        ].map((s) => (
          <div key={s.label} className="section-card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B', fontWeight: 500, marginBottom: '6px', textTransform: 'uppercase' }}>{s.label}</div>
                <div style={{ fontSize: '28px', fontWeight: 700 }}>{s.value}</div>
              </div>
              <div style={{ padding: '8px', background: s.bg, color: s.color, borderRadius: '8px' }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { label: 'All', value: undefined },
          { label: 'Pending Pre-Auth', value: 'pending_preauth' },
          { label: 'Locked', value: 'preauthorized' },
          { label: 'Confirmed', value: 'confirmed' },
          { label: 'Captured', value: 'captured' },
          { label: 'Shipped', value: 'shipped' },
          { label: 'Delivered', value: 'delivered' },
          { label: 'Released', value: 'released' },
        ].map((f) => (
          <button
            key={f.label}
            onClick={() => setStatusFilter(f.value)}
            className="btn"
            style={{
              padding: '8px 20px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              background: statusFilter === f.value ? '#1C1C1C' : 'transparent',
              color: statusFilter === f.value ? '#fff' : '#1C1C1C',
              fontWeight: 600,
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--sidebar-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}</span>
          <span style={{ fontSize: '12px', color: '#64748B' }}>Data sourced directly from the smart order table</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--sidebar-border)' }}>
              <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Created</th>
              <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Customer</th>
              <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Items</th>
              <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Status</th>
              <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px', textAlign: 'right' }}>Total</th>
              <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>Loading...</td></tr>
            ) : filteredOrders.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>No smart orders found.</td></tr>
            ) : filteredOrders.map((order) => (
              <tr
                key={order.id}
                style={{ borderBottom: '1px solid var(--sidebar-border)', cursor: 'pointer' }}
                onClick={() => setDetailOrderId(order.id)}
              >
                <td style={{ padding: '16px 20px', color: '#64748B', whiteSpace: 'nowrap' }}>
                  {new Date(order.created_at).toLocaleDateString()}
                </td>
                <td style={{ padding: '16px 20px' }}>
                  <div style={{ fontWeight: 600, color: '#0F172A' }}>{order.user_id}</div>
                  <div style={{ fontSize: '12px', color: '#64748B' }}>Order #{order.id.split('-')[0].toUpperCase()}</div>
                </td>
                <td style={{ padding: '16px 20px', maxWidth: '280px' }}>
                  <div style={{ display: 'grid', gap: '4px' }}>
                    {order.items.slice(0, 2).map((item, idx) => (
                      <div key={`${order.id}-${idx}`} style={{ fontSize: '12px', color: '#334155' }}>
                        {item.retailer} • £{item.price.toFixed(2)}
                        {(item.quantity ?? 1) > 1 && <> • ×{item.quantity}</>}
                        {(item.size || item.color || item.discount_code) && (
                          <div style={{ color: '#64748B' }}>
                            {[item.size && `Size: ${item.size}`, item.color && `Color: ${item.color}`, item.discount_code && `Code: ${item.discount_code}`]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        )}
                      </div>
                    ))}
                    {order.items.length > 2 && <div style={{ fontSize: '12px', color: '#64748B' }}>+{order.items.length - 2} more</div>}
                  </div>
                </td>
                <td style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                    <span className="status-pill" style={{
                      background: `${statusColors[order.status] || '#64748B'}15`,
                      color: statusColors[order.status] || '#64748B',
                      display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content',
                    }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }}></div>
                      {order.status.toUpperCase()}
                    </span>
                    {order.status === 'delivered' && order.invoice_status === 'rejected' && (
                      <span className="status-pill" style={{ background: '#FEF2F2', color: '#DC2626' }}>INVOICE REJECTED</span>
                    )}
                    {order.reward_credited && (
                      <span className="status-pill" style={{ background: '#DCFCE7', color: '#16A34A' }}>
                        REWARD +£{(order.reward_credited_amount ?? 0).toFixed(2)}
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '16px 20px', textAlign: 'right', fontWeight: 700 }}>
                  £{order.total_amount.toFixed(2)}
                </td>
                <td style={{ padding: '16px 20px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                    {order.status === 'pending_preauth' && order.payment_method === 'bank_transfer' && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        disabled={actionOrderId === order.id}
                        onClick={() => handleConfirmBankTransfer(order)}
                      >
                        {actionOrderId === order.id ? 'Confirming...' : 'Confirm Bank Transfer'}
                      </button>
                    )}
                    {order.status === 'preauthorized' && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        disabled={actionOrderId === order.id}
                        onClick={() => handleConfirmOrder(order)}
                      >
                        {actionOrderId === order.id ? 'Confirming...' : 'Confirm Order'}
                      </button>
                    )}
                    {order.status === 'confirmed' && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        disabled={actionOrderId === order.id}
                        onClick={() => handleCapture(order)}
                      >
                        {actionOrderId === order.id ? 'Capturing...' : 'Capture Payment'}
                      </button>
                    )}
                    {order.status === 'captured' && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        onClick={() => openShipModal(order)}
                      >
                        {order.package_id && packages[order.package_id]
                          ? `Ship Order (${packages[order.package_id].order_ids.length} orders in package)`
                          : 'Ship Order'}
                      </button>
                    )}
                    {order.status === 'captured' && !order.package_id && !order.shipping_fee_paid && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        onClick={() => openFeeModal('order', order.id, order.admin_base_shipping_fee)}
                      >
                        {order.admin_base_shipping_fee != null ? 'Update Shipping Fee' : 'Set Shipping Fee'}
                      </button>
                    )}
                    {order.package_id && packages[order.package_id]
                      && !packages[order.package_id].shipping_fee_paid
                      && filteredOrders.find((o) => o.package_id === order.package_id)?.id === order.id && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        onClick={() => openFeeModal('package', order.package_id!, packages[order.package_id!].admin_base_shipping_fee)}
                      >
                        {packages[order.package_id].admin_base_shipping_fee != null
                          ? `Update Package Shipping Fee (${packages[order.package_id].order_ids.length} orders)`
                          : `Set Package Shipping Fee (${packages[order.package_id].order_ids.length} orders)`}
                      </button>
                    )}
                    {order.shipping_fee_payment_method === 'bank_transfer' && !order.shipping_fee_paid && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        disabled={actionOrderId === order.id}
                        onClick={() => handleConfirmShippingBankTransfer(order)}
                      >
                        {actionOrderId === order.id ? 'Confirming...' : 'Confirm Shipping Transfer'}
                      </button>
                    )}
                    {order.package_id && packages[order.package_id]
                      && packages[order.package_id].shipping_fee_payment_method === 'bank_transfer'
                      && !packages[order.package_id].shipping_fee_paid
                      && filteredOrders.find((o) => o.package_id === order.package_id)?.id === order.id && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        disabled={actionPackageId === order.package_id}
                        onClick={() => handleConfirmPackageBankTransfer(packages[order.package_id!])}
                      >
                        {actionPackageId === order.package_id ? 'Confirming...' : `Confirm Package Shipping Transfer (${packages[order.package_id].order_ids.length} orders)`}
                      </button>
                    )}
                    {order.status === 'shipped' && (
                      <>
                        <button
                          className="btn btn-outline"
                          style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                          onClick={() => openTrackingModal(order)}
                        >
                          Update Tracking
                        </button>
                        <button
                          className="btn btn-outline"
                          style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                          disabled={actionOrderId === order.id}
                          onClick={() => handleMarkDelivered(order)}
                        >
                          {actionOrderId === order.id
                            ? 'Marking...'
                            : order.package_id && packages[order.package_id]
                            ? `Mark Delivered (${packages[order.package_id].order_ids.length} orders in package)`
                            : 'Mark Delivered'}
                        </button>
                      </>
                    )}
                    {order.status === 'delivered' && order.invoice_status !== 'verified' && order.invoice_status !== 'rejected' && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        onClick={() => openInvoiceModal(order)}
                      >
                        Verify Invoice
                      </button>
                    )}
                    {(order.status === 'pending_preauth' || order.status === 'preauthorized' || order.status === 'confirmed') && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto', color: '#DC2626', borderColor: '#FCA5A5' }}
                        disabled={actionOrderId === order.id}
                        onClick={() => handleRelease(order)}
                      >
                        {actionOrderId === order.id ? 'Releasing...' : 'Release Pre-auth'}
                      </button>
                    )}
                    <button
                      className="btn btn-outline"
                      style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                      onClick={() => openEditModal(order)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto', color: '#DC2626', borderColor: '#FCA5A5' }}
                      onClick={() => setDeleteOrderId(order.id)}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {deleteOrderId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '400px', margin: '20px', textAlign: 'center' }}>
            <h2 className="card-title" style={{ color: '#DC2626' }}>Delete Smart Order</h2>
            <p style={{ margin: '16px 0', color: '#64748B', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to permanently delete this smart order?<br />
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setDeleteOrderId(null)} disabled={deletingOrder}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#DC2626' }}
                onClick={handleDeleteOrder}
                disabled={deletingOrder}
              >
                {deletingOrder ? 'Deleting...' : 'Delete Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {shipOrderId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Ship Order</h2>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-8px', marginBottom: '16px' }}>
              Provide the tracking number for this shipment (required).
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="ship-tracking-number-input">Tracking Number</label>
              <input id="ship-tracking-number-input" className="form-input" value={shipForm.tracking_number} onChange={(e) => setShipForm({ ...shipForm, tracking_number: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="ship-carrier-input">Carrier</label>
              <input id="ship-carrier-input" className="form-input" value={shipForm.carrier} onChange={(e) => setShipForm({ ...shipForm, carrier: e.target.value })} placeholder="DHL, FedEx, Royal Mail..." />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="ship-tracking-url-input">Tracking URL</label>
              <input id="ship-tracking-url-input" className="form-input" value={shipForm.tracking_url} onChange={(e) => setShipForm({ ...shipForm, tracking_url: e.target.value })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setShipOrderId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitShip} disabled={savingShip || !shipForm.tracking_number}>
                {savingShip ? 'Shipping...' : 'Ship Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {invoiceOrderId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Verify Invoice</h2>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-8px', marginBottom: '16px' }}>
              Did the supplier issue a compliant Company Invoice? Only a compliant invoice releases the customer's Bonus Reward.
            </p>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="radio" checked={invoiceForm.compliant} onChange={() => setInvoiceForm({ ...invoiceForm, compliant: true })} />
                Compliant
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="radio" checked={!invoiceForm.compliant} onChange={() => setInvoiceForm({ ...invoiceForm, compliant: false })} />
                Rejected
              </label>
            </div>
            {invoiceForm.compliant ? (
              <div className="form-group">
                <label className="form-label" htmlFor="invoice-actual-amount">Actual Invoice Amount (£)</label>
                <input
                  id="invoice-actual-amount"
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={invoiceForm.actual_invoice_amount}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, actual_invoice_amount: e.target.value })}
                />
                <p style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                  Defaults to the customer's original quote — edit this if the supplier's
                  invoice shows a different real purchase price. The Bonus Reward is
                  calculated off this amount.
                </p>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label" htmlFor="invoice-rejection-reason">Rejection Reason</label>
                <textarea
                  id="invoice-rejection-reason"
                  className="form-input"
                  style={{ minHeight: '80px' }}
                  value={invoiceForm.rejection_reason}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, rejection_reason: e.target.value })}
                  placeholder="e.g. Brand's retail policy prohibits issuing VAT invoices"
                />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setInvoiceOrderId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitInvoice} disabled={savingInvoice || (!invoiceForm.compliant && !invoiceForm.rejection_reason)}>
                {savingInvoice ? 'Saving...' : invoiceForm.compliant ? 'Confirm & Release Reward' : 'Reject Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {trackingOrderId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Update Tracking</h2>
            <div className="form-group">
              <label className="form-label" htmlFor="tracking-number-input">Tracking Number</label>
              <input id="tracking-number-input" className="form-input" value={trackingForm.tracking_number} onChange={(e) => setTrackingForm({ ...trackingForm, tracking_number: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="tracking-carrier-input">Carrier</label>
              <input id="tracking-carrier-input" className="form-input" value={trackingForm.carrier} onChange={(e) => setTrackingForm({ ...trackingForm, carrier: e.target.value })} placeholder="DHL, FedEx, Royal Mail..." />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="tracking-url-input">Tracking URL</label>
              <input id="tracking-url-input" className="form-input" value={trackingForm.tracking_url} onChange={(e) => setTrackingForm({ ...trackingForm, tracking_url: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="tracking-delivery-status-select">Delivery Status</label>
              <select id="tracking-delivery-status-select" className="form-input" value={trackingForm.delivery_status} onChange={(e) => setTrackingForm({ ...trackingForm, delivery_status: e.target.value })}>
                <option value="">-- Select --</option>
                <option value="pending">Pending</option>
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="exception">Exception</option>
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setTrackingOrderId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitTracking} disabled={savingTracking}>
                {savingTracking ? 'Saving...' : 'Save Tracking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOrder && editForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '640px', margin: '20px', maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 className="card-title">Edit Order #{editOrder.id.split('-')[0].toUpperCase()}</h2>

            {!editContentAllowed && (
              <p style={{ fontSize: '12px', color: '#DC2626', marginTop: '-8px', marginBottom: '16px' }}>
                Items and total can no longer be edited — this order is already {editOrder.status}.
              </p>
            )}
            <fieldset disabled={!editContentAllowed} style={{ border: 'none', padding: 0, margin: 0, opacity: editContentAllowed ? 1 : 0.5 }}>
              <div style={{ display: 'grid', gap: '16px', marginBottom: '16px' }}>
                {editForm.items.map((item, idx) => (
                  <div key={item.id || idx} style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '16px', display: 'grid', gap: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Retailer / Name</label>
                        <input className="form-input" value={item.retailer} onChange={(e) => updateEditItem(idx, 'retailer', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Category</label>
                        <input className="form-input" value={item.category} onChange={(e) => updateEditItem(idx, 'category', e.target.value)} />
                      </div>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">URL</label>
                      <input className="form-input" value={item.url} onChange={(e) => updateEditItem(idx, 'url', e.target.value)} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Price (£)</label>
                        <input type="number" step="0.01" className="form-input" value={item.price} onChange={(e) => updateEditItem(idx, 'price', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Quantity</label>
                        <input type="number" min="1" className="form-input" value={item.quantity} onChange={(e) => updateEditItem(idx, 'quantity', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Size</label>
                        <input className="form-input" value={item.size} onChange={(e) => updateEditItem(idx, 'size', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Color</label>
                        <input className="form-input" value={item.color} onChange={(e) => updateEditItem(idx, 'color', e.target.value)} />
                      </div>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Discount Code</label>
                      <input className="form-input" value={item.discount_code} onChange={(e) => updateEditItem(idx, 'discount_code', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">Total Amount (£)</label>
                <input type="number" step="0.01" className="form-input" value={editForm.total_amount} onChange={(e) => setEditForm({ ...editForm, total_amount: e.target.value })} />
              </div>
            </fieldset>

            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid var(--sidebar-border)' }} />

            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Shipping Fee</h3>
            {!editShippingFeeAllowed && (
              <p style={{ fontSize: '12px', color: '#DC2626', marginTop: 0, marginBottom: '16px' }}>
                The shipping fee has already been paid and can no longer be edited.
              </p>
            )}
            <fieldset disabled={!editShippingFeeAllowed} style={{ border: 'none', padding: 0, margin: 0, opacity: editShippingFeeAllowed ? 1 : 0.5 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Shipping Fee Amount (£)</label>
                  <input type="number" step="0.01" className="form-input" value={editForm.shipping_fee_amount} onChange={(e) => setEditForm({ ...editForm, shipping_fee_amount: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Shipping Fee Payment Method</label>
                  <select className="form-input" value={editForm.shipping_fee_payment_method} onChange={(e) => setEditForm({ ...editForm, shipping_fee_payment_method: e.target.value })}>
                    <option value="">-- Select --</option>
                    <option value="card">Card (Stripe)</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="reward">Reward Balance</option>
                  </select>
                </div>
              </div>
            </fieldset>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => { setEditOrderId(null); setEditForm(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={submitEdit} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {feeTarget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '420px', margin: '20px' }}>
            <h2 className="card-title">{feeTarget.type === 'package' ? 'Set Package Shipping Fee' : 'Set Shipping Fee'}</h2>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-8px', marginBottom: '16px' }}>
              Enter the real carrier cost after packing. The customer sees this amount
              (plus a 3% card surcharge if they pay by card) and pays it themselves.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="admin-shipping-fee-input">Base Shipping Fee (£)</label>
              <input id="admin-shipping-fee-input" type="number" step="0.01" className="form-input" value={feeForm} onChange={(e) => setFeeForm(e.target.value)} placeholder="0.00" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setFeeTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitFee} disabled={savingFee || !feeForm || Number(feeForm) <= 0}>
                {savingFee ? 'Saving...' : 'Save Fee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '640px', margin: '20px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Order #{detailOrder.id.split('-')[0].toUpperCase()}</h2>
              <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={() => setDetailOrderId(null)}>Close</button>
            </div>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: 0, marginBottom: '20px' }}>
              Customer: {detailOrder.user_id} &middot; Total £{detailOrder.total_amount.toFixed(2)}
            </p>
            <div style={{ display: 'grid', gap: '16px' }}>
              {detailOrder.items.map((item, idx) => (
                <div key={item.id || idx} style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '16px', display: 'grid', gridTemplateColumns: item.image_filename ? '96px 1fr' : '1fr', gap: '16px' }}>
                  {item.image_filename && item.id && itemImageUrls[item.id] && (
                    <img src={itemImageUrls[item.id]} alt={item.retailer} style={{ width: '96px', height: '96px', objectFit: 'cover', borderRadius: '6px' }} />
                  )}
                  <div style={{ display: 'grid', gap: '6px', fontSize: '13px' }}>
                    <div style={{ fontWeight: 700, fontSize: '15px' }}>{item.retailer}</div>
                    <div style={{ color: '#64748B' }}>{item.category}</div>
                    <div>
                      <a href={item.url} target="_blank" rel="noreferrer" style={{ color: '#2563EB', wordBreak: 'break-all' }}>{item.url}</a>
                    </div>
                    <div>Price: £{item.price.toFixed(2)} &middot; Quantity: {item.quantity ?? 1}</div>
                    {(item.size || item.color || item.discount_code) && (
                      <div style={{ color: '#334155' }}>
                        {[item.size && `Size: ${item.size}`, item.color && `Color: ${item.color}`, item.discount_code && `Discount code: ${item.discount_code}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProcurementRequests;
